import { useRoute } from 'vue-router'
import {
  ABTestInputCondition,
  ABTestUserActualCondition,
  ABTestGroup,
  ABTestOutput,
  abTestGlobalKey,
} from './abtestV2'
import useABTestStoreV2 from './store'
import useDeviceStore from '@/store/device'
import useRequestLocationStore from '@/store/requestLocation'
import Analytics from '@/services/analytics'
import Logger from '@/services/log'
import LocationService from '@/services/location'
import { isOnServer } from '@/utils/ssr'
import { getShopCode } from '@/utils/shop'

export default function useABTestV2() {
  const store = useABTestStoreV2()
  const deviceStore = useDeviceStore()
  const locationStore = useRequestLocationStore()
  const route = useRoute()

  /**
   * Hàm chính để chạy một AB Test.
   * Các tham số runtime (thiết bị, query, v.v.) sẽ được tự động lấy.
   */
  function run(input: ABTestInputCondition): void {
    if (isOnServer) return

    try {
      const actualCondition = _getActualCondition()
      console.log('actualCondition.ip', actualCondition.ip)

      // Kiểm tra Whitelist IP (luôn chứa localhost)
      if (actualCondition.ip && input.ipWhitelist?.includes(actualCondition.ip)) {
        return
      }

      // Bước 1: Kiểm tra user có đang tham gia chiến dịch nào không
      if (isInOtherCampaign()) {
        // Branch (1.1): Đang tham gia chiến dịch -> Kiểm tra thời hạn của chiến dịch đang tham gia
        if (
          store.endDate &&
          store.startDate &&
          (isDatePassed(store.endDate) || !isDatePassed(store.startDate))
        ) {
          // Branch (1.1.1): Chiến dịch đang tham gia đã quá hạn -> Xóa -> Chuyển qua Bước 2
          store.clearCampaign()
        } else {
          // Branch (1.1.2): Chiến dịch đang tham gia chưa quá hạn -> Kiểm tra có phải chiến dịch đang tham gia là chiến dịch mục tiêu không
          if (store.code === input.code) {
            // Branch (1.1.2.1): Chiến dịch đang tham gia là chiến dịch mục tiêu
            if (store.output?.inTest) {
              // Branch (1.1.2.1.1): User vẫn đang tham gia chiến dịch này
              if (input.endIfPurchased && actualCondition.isPurchased) {
                // Branch (1.1.2.1.1.1): Nếu đã mua hàng thành công trong khoảng thời gian tham gia chiến dịch hiện tại thì thoát khỏi luồng AB test hiện tại, không được tham gia bất kỳ chiến dịch nào khác cho đến khi chiến dịch hiện tại kết thúc
                store.setCampaign({
                  output: {
                    variant: undefined,
                  },
                })
                return
              } else {
                // Branch (1.1.2.1.1.2): Các trường hợp còn lại giữ nguyên thông tin chiến dịch hiện tại -> Thoát
                // Hầu hết các TH rơi vào đây là user đang trong thời gian tham gia chiến dịch này nhưng chưa mua hàng
                return
              }
            } else if (store.output?.inTest === false) {
              // Branch (1.1.2.1.2): User đã từng được xem xét tham gia chiến dịch này nhưng không được chọn
              if (!store.output?.variant) {
                // Branch (1.1.2.1.2.1): lý do rơi vào TH này:
                // 1. Không rơi vào xác suất được chọn
                // Giữ nguyên thông tin chiến dịch hiện tại -> Thoát
                return
              } else {
                // Branch (1.1.2.1.2.2): Các trường hợp còn lại giữ nguyên thông tin chiến dịch hiện tại -> Thoát
                return
              }
            } else {
              // Branch (1.1.2.1.3): Các trường hợp còn lại giữ nguyên thông tin chiến dịch hiện tại -> Thoát
              return
            }
          } else {
            // Branch (1.1.2.2): Chiến dịch đang tham gia không phải là chiến dịch mục tiêu -> Thoát
            return
          }
        }
      } else {
        // Branch (1.2): Không có chiến dịch nào đang chạy -> Chuyển qua Bước 2
      }

      // Đến bước này thì chiến dịch hiện tại là chiến dịch mục tiêu
      // Và chắc chắn đây là lần đầu tiên user tham gia chiến dịch hiện tại
      store.clearCampaign()
      // Bước 2: Kiểm tra thời gian của chiến dịch
      if (!isDatePassed(input.startDate) || isDatePassed(input.endDate)) {
        // Nếu chiến dịch chưa đến ngày hoặc đã quá hạn -> Thoát
        return
      }

      // Kiểm tra yêu cầu đầu vào với điều kiện thực tế của người dùng
      // Bước 3: Kiểm tra loại thiết bị cho phép
      if (
        input.allowedDevices?.length &&
        !checkList(input.allowedDevices, actualCondition.deviceType)
      ) {
        // Không hợp lệ -> Thoát
        return
      }

      // Bước 4: Kiểm tra quốc gia cho phép
      if (
        input.allowedCountries?.length &&
        !checkList(input.allowedCountries, actualCondition.countryCode)
      ) {
        // Không hợp lệ -> Thoát
        return
      }

      // Bước 4.0.1: Kiểm tra store cho phép
      if (
        input.allowedStores?.length &&
        !checkList(input.allowedStores, getShopCode())
      ) {
        // Không hợp lệ -> Thoát
        return
      }

      // Bước 4.1: Kiểm tra URL cho phép
      if (input.allowedUrls?.length) {
        const currentPath = !isOnServer ? window?.location?.pathname : ''
        const isUrlAllowed = input.allowedUrls.some((url) => {
          return currentPath.includes(url)
        })
        if (!isUrlAllowed) {
          return
        }
      }

      // Bước 5: Kiểm tra UTM yêu cầu
      if (input.requiredUtms?.length) {
        const match = input.requiredUtms.some((ruleSet) => {
          for (const key in ruleSet) {
            if (
              ((actualCondition.query?.[key] as string) || '').toLowerCase() !==
              ruleSet[key].toLowerCase()
            ) {
              // tồn tại key có value khác yêu cầu -> Thoát
              return false
            }
          }
          // có item match -> Tiếp tục
          return true
        })
        if (!match) {
          // Không có item nào match -> Thoát
          return
        }
      }

      // Bước 6: Kiểm tra UTM loại trừ
      if (input.excludeUtms && input.excludeUtms.length > 0) {
        const match = input.excludeUtms.some((ruleSet) => {
          for (const key in ruleSet) {
            if (
              ((actualCondition.query?.[key] as string) || '').toLowerCase() !==
              ruleSet[key].toLowerCase()
            ) {
              // tồn tại key có value khác loại trừ -> Tiếp tục
              return false
            }
          }
          // có item match -> Thoát
          return true
        })
        if (match) {
          // có item nằm trong danh sách loại trừ -> Thoát
          return
        }
      }

      // Bước 7: Kiểm tra Stop Date (Ngày dừng nhận user)
      if (isDatePassed(input.stopDate)) {
        return
      }

      // Bước 8: Chọn ngẫu nhiên theo xác suất (ví dụ: 5%)
      const rate = input.trafficRate ?? 1
      if (Math.random() > rate) {
        // Ngoài xác suất được chọn -> Không được tham gia chiến dịch này nữa (có thể tham gia chiến dịch khác)
        // Chỗ này có thể gặp bug với case: Ngoài xác suất được chọn chiến dịch này -> gặp chiến dịch khác -> cũng Ngoài xác suất được chọn -> gặp lại chiến dịch này -> vào xác suất được chọn
        // Solution: lưu lịch sử các chiến dịch tham gia đến được bước này (TODO)
        store.setCampaign({
          input,
          output: {
            inTest: false,
          },
        })
        return
      }
      // Rơi vào xác suất được chọn
      // Người dùng đã đạt các điều kiện để tham gia chiến dịch

      // Bước 9: Chọn ngẫu nhiên variant
      const variant = selectVariant(input.variants)

      // Bước 10: Xử lý Dynamic Meta cho việc tracking
      const dynamicMeta = input.getDynamicMeta?.(actualCondition)

      const output: ABTestOutput = {
        variant,
        assignedAt: Date.now(),
        inTest: true,
        subExprId: dynamicMeta?.subExprId || '',
        subExprName: dynamicMeta?.subExprName || '',
      }

      // Bước 11: Lưu trữ và Track
      store.setCampaign({
        input,
        output,
        actualCondition,
      })
      if (input.isTrack !== false) {
        trackEntry(input, output)
      }
    } catch (error) {
      Logger.error(
        `[ABTestV2] Critical error in campaign: ${input.code}`,
        error
      )
    }
  }

  function init() {
    if (isOnServer) return

    // Đồng bộ từ localStorage
    store.syncFromLocalStorage()
  }

  // --- Các hàm bổ trợ (Logic nghiệp vụ lõi) ---

  function isDatePassed(dateStr: string): boolean {
    if (!dateStr) return false
    return new Date().getTime() >= new Date(dateStr).getTime()
  }

  /**
   * Get điều kiện hiện tại của thiết bị người dùng.
   */
  function _getActualCondition(): ABTestUserActualCondition {
    return {
      query: route ? route.query : {},
      deviceType: deviceStore.isMobile
        ? abTestGlobalKey.deviceMobile
        : abTestGlobalKey.deviceDesktop,
      countryCode: locationStore.countryCode,
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      isPurchased: store.isPurchased, // Kiểm tra từ trạng thái chiến dịch đang hoạt động
      ip: LocationService.location?.ip,
    }
  }

  /**
   * Chọn variant ngẫu nhiên dựa trên tỷ lệ.
   */
  function selectVariant(
    variants?: { code: ABTestGroup; rate?: number }[]
  ): ABTestGroup {
    const defaultVariants = [
      { code: abTestGlobalKey.groupControl, rate: 0.5 },
      { code: abTestGlobalKey.groupTreatment, rate: 0.5 },
    ]
    const list = variants || defaultVariants

    const random = Math.random()
    let rateSum = 0

    for (const v of list) {
      const rate = v.rate ?? 0
      rateSum += rate
      if (random < rateSum) return v.code
    }
    return list[list.length - 1].code
  }

  function checkList(allowedList: string[], value?: string): boolean {
    if (!value) return false
    return allowedList.map((a) => a.toLowerCase()).includes(value.toLowerCase())
  }

  /**
   * Kiểm tra xem user có đang tham gia một chiến dịch khác mâu thuẫn không.
   * Node (CheckMatch - 1.1.2.2)
   */
  function isInOtherCampaign(): boolean {
    if (isOnServer) return false
    return !!store.code
  }

  function trackEntry(context: ABTestInputCondition, status: ABTestOutput) {
    const extraTracking = context.extraTracking ? context.extraTracking() : {}
    const payload = {
      ...extraTracking,
      expr_id: context.exprId,
      expr_name: context.exprName,
      sub_expr_id: status.subExprId,
      sub_expr_name: status.subExprName,
      variant: status.variant,
    }
    Analytics.track(abTestGlobalKey.eventExprEntry, payload)
    const anonymousId = Analytics?.getAnonymousId()

    Analytics.identify(anonymousId, {
      in_test: true,
      current_test_attribute: payload,
    })
  }

  return {
    run,
    init,
    isDatePassed,
  }
}
